import { eachDayOfInterval, format } from 'date-fns'
import { formatTimeForDisplay } from 'sharedKernel/handlers/date'
import { extractListRows } from 'domain/feature/hub/company/meeting-room/shared/apiResponse'
import { postCall } from 'sharedKernel/api'
import {
  AvailabilityCalendarEvent,
  AvailabilityCalendarRoom,
} from '../AvailabilityCalendarModal/types'
import {
  DEFAULT_CLOSE_TIME,
  DEFAULT_OPEN_TIME,
} from '../AvailabilityCalendarModal/scheduleConfig'
import { getSpaceColor } from '../AvailabilityCalendarModal/spaceColors'
import { PUBLIC_MEETING_ROOM_API } from './constants'
import { PublicReservationByStatusDto, PublicReservationByStatusRequestPayload } from './types'

export interface YearMonth {
  year: number
  month: number
}

export const yearMonthKey = (value: YearMonth): string => `${value.year}-${value.month}`

export const addYearMonth = (value: YearMonth, delta: number): YearMonth => {
  const date = new Date(value.year, value.month - 1 + delta, 1)
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
  }
}

export const getMonthWindow = (focused: YearMonth): YearMonth[] => [
  addYearMonth(focused, -1),
  focused,
  addYearMonth(focused, 1),
]

const asDto = (item: unknown): PublicReservationByStatusDto | null => {
  if (item == null || typeof item !== 'object') return null
  return item as PublicReservationByStatusDto
}

const parseDateOnly = (value?: string | null): Date | null => {
  const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match == null) return null

  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  return new Date(year, month, day, 0, 0, 0, 0)
}

const combineDateAndTime = (date: Date, time?: string | null): Date | null => {
  const displayTime = formatTimeForDisplay(time ?? '') || '00:00'
  const [hourRaw, minuteRaw] = displayTime.split(':')
  const hour = Number(hourRaw)
  const minute = Number(minuteRaw)

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    Number.isFinite(hour) ? hour : 0,
    Number.isFinite(minute) ? minute : 0,
    0,
    0
  )
}

const toDayKey = (date: Date): string => format(date, 'yyyy-MM-dd')

export const buildListByStatusPayload = (
  yearMonth: YearMonth,
  meetingRoomIds?: string[]
): PublicReservationByStatusRequestPayload => ({
  year: yearMonth.year,
  month: yearMonth.month,
  ...(meetingRoomIds != null && meetingRoomIds.length > 0
    ? { meetingRoomIds }
    : {}),
})

export const mapReservationDtoToCalendarEvents = (
  item: unknown,
  title: string,
  roomIndexById: Map<string, number>
): AvailabilityCalendarEvent[] => {
  const dto = asDto(item)
  if (dto == null) return []

  const id = dto.id != null ? String(dto.id) : ''
  const roomId = dto.meetingRoomId != null ? String(dto.meetingRoomId) : ''
  const startDate = parseDateOnly(dto.reservationDate)
  if (!id || !roomId || startDate == null) return []

  const endDate = parseDateOnly(dto.endDate) ?? startDate
  const rangeEnd = endDate < startDate ? startDate : endDate

  const startTime = formatTimeForDisplay(dto.startTime ?? undefined) || DEFAULT_OPEN_TIME
  const endTime = formatTimeForDisplay(dto.endTime ?? undefined) || DEFAULT_CLOSE_TIME
  const openTime = DEFAULT_OPEN_TIME
  const closeTime = DEFAULT_CLOSE_TIME

  const roomIndex = roomIndexById.get(roomId) ?? -1
  const roomName = dto.meetingRoomName?.trim() || ''
  const color = getSpaceColor(roomId, roomIndex)

  const days = eachDayOfInterval({ start: startDate, end: rangeEnd })
  const isSingleDay = days.length === 1

  return days
    .map(day => {
      const dayStartTime = isSingleDay || toDayKey(day) === toDayKey(startDate) ? startTime : openTime
      const dayEndTime = isSingleDay || toDayKey(day) === toDayKey(rangeEnd) ? endTime : closeTime

      const start = combineDateAndTime(day, dayStartTime)
      const end = combineDateAndTime(day, dayEndTime)
      if (start == null || end == null || end <= start) return null

      const event: AvailabilityCalendarEvent = {
        id: `${id}:${toDayKey(day)}`,
        title,
        start,
        end,
        roomId,
        roomName,
        color,
      }
      return event
    })
    .filter((event): event is AvailabilityCalendarEvent => event != null)
}

export const fetchReservationsForMonth = async (
  yearMonth: YearMonth,
  title: string,
  rooms: AvailabilityCalendarRoom[]
): Promise<AvailabilityCalendarEvent[]> => {
  const meetingRoomIds = rooms.map(room => room.id).filter(Boolean)
  const response = await postCall({
    url: PUBLIC_MEETING_ROOM_API.LIST_BY_STATUS,
    isPublic: true,
    data: buildListByStatusPayload(yearMonth, meetingRoomIds),
  })

  const roomIndexById = new Map(rooms.map((room, index) => [room.id, index]))
  const events: AvailabilityCalendarEvent[] = []
  const seen = new Set<string>()

  extractListRows(response.data).forEach(item => {
    mapReservationDtoToCalendarEvents(item, title, roomIndexById).forEach(event => {
      if (seen.has(event.id)) return
      seen.add(event.id)
      events.push(event)
    })
  })

  return events.sort((a, b) => a.start.getTime() - b.start.getTime())
}
